import React from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  useDisclosure,
  Button,
  Spinner,
  Image,
} from "@chakra-ui/react";
import NoticeDetailsModal from "./NoticeDetailsModal";
import { CalenderIcon } from "@root/assets/icons";
import { DownloadIcon } from "@root/assets/icons/downloadIcon";
import { useTranslation } from "react-i18next";
import useNotice from "@root/hooks/useNotice";
import useMediaQ from "@root/hooks/useMediaQ";
import noticeThumbnail from "@root/assets/images/notice-thumbnail.png";

type NoticeType = "alert" | "info" | "campaign";

export interface NoticeCardProps {
  notice: {
    id?: number;
    title?: string;
    description?: string;
    createdAt?: string;
    views?: number;
    type?: NoticeType;
    noticeType?: { name?: string };
    details?: string;
    documents?: Array<{
      id: number;
      signedUrl: string;
      originalName: string;
      fileName: string;
    }>;
  };
  showThumbnail?: boolean;
  compact?: boolean;
  [key: string]: any;
}

const formatDateToYYYYMMDD = (dateString?: string) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const NoticeCard: React.FC<NoticeCardProps> = ({
  notice,
  showThumbnail = false,
  compact = false,
  ...rest
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { downloadDocumentsZip, isDownloadingNotice } = useNotice();
  const { t } = useTranslation("common");
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const handleDownloadFiles = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!notice?.documents || notice.documents.length === 0) return;

    const documentIds = notice.documents.map((doc) => doc.id);
    const sanitizedTitle =
      notice.title?.replace(/[^a-z0-9]/gi, "_").toLowerCase() || "notice_files";

    await downloadDocumentsZip(
      documentIds,
      notice?.id,
      `${sanitizedTitle}.zip`
    );
  };

  const isDownloading = isDownloadingNotice(notice?.id);

  const hasDocuments = notice?.documents && notice.documents.length > 0;

  // Mobile layout - matches course card mobile layout
  if (isMobile && showThumbnail) {
    return (
      <>
        <Box
          bg="white"
          borderRadius="12px"
          border="1px solid #CDD6DC"
          p="12px"
          boxShadow="sm"
          display="flex"
          gap="12px"
          cursor="pointer"
          transition="box-shadow 0.2s"
          _hover={{ boxShadow: "md" }}
          onClick={onOpen}
          position="relative"
          {...rest}
        >
          <Box
            w="120px"
            h="120px"
            flexShrink={0}
            borderRadius="12px"
            overflow="hidden"
            position="relative"
          >
            <Image
              src={noticeThumbnail}
              alt={notice?.title}
              w="100%"
              h="100%"
              objectFit="contain"
            />
          </Box>

          <Flex direction="column" flex="1" justify="space-between">
            <Box>
              <Text fontWeight={600} fontSize="16px" color="#2F2F34" noOfLines={1} mb={1}>
                {notice?.title}
              </Text>
              <Text
                color="#5C6670"
                fontSize="14px"
                fontWeight={400}
                mb={2}
                noOfLines={2}
              >
                {notice?.description}
              </Text>
              <Flex direction="column" gap={1} mb={2}>
                <Flex
                  align="center"
                  gap={2}
                  color="#798692"
                  fontSize="12px"
                >
                  <Icon as={CalenderIcon} boxSize={3} />
                  <Text>{formatDateToYYYYMMDD(notice?.createdAt)}</Text>
                </Flex>
                <Text fontWeight="400" fontSize="12px" textColor="#5C6670">
                  {notice?.noticeType?.name}
                </Text>
              </Flex>
            </Box>
            {hasDocuments && (
              <Button
                size="sm"
                variant="outline"
                borderColor="#1819B4"
                color="#1819B4"
                fontWeight={600}
                fontSize="12px"
                leftIcon={
                  isDownloading ? (
                    <Spinner size="xs" color="#1819B4" />
                  ) : (
                    <DownloadIcon boxSize={3} color="#1819B4" />
                  )
                }
                onClick={handleDownloadFiles}
                borderRadius="8px"
                height="28px"
                px="12px"
                py="4px"
                _hover={{ bg: "#F5F7FA" }}
                isDisabled={isDownloading}
                mt="4px"
                width="100%"
              >
                {isDownloading
                  ? t("notice.downloading")
                  : t("notice.downloadFiles")}
              </Button>
            )}
          </Flex>
        </Box>
        <NoticeDetailsModal
          formatDateToYYYYMMDD={formatDateToYYYYMMDD}
          isOpen={isOpen}
          onClose={onClose}
          notice={notice}
        />
      </>
    );
  }

  // Desktop layout - matches course card desktop layout
  return (
    <>
      <Box
        bg="white"
        borderRadius={compact ? "10px" : "12px"}
        border={compact ? "1px solid #E2E8F0" : "1px solid #CDD6DC"}
        p={compact ? "10px 12px" : showThumbnail ? "8px" : 5}
        boxShadow="sm"
        minH={compact ? "80px" : "120px"}
        display="flex"
        flexDirection="column"
        cursor="pointer"
        transition="box-shadow 0.2s"
        _hover={{ boxShadow: "md" }}
        onClick={onOpen}
        position="relative"
        {...rest}
      >
        {showThumbnail && (
          <Box
            position="relative"
            h="220px"
            flexShrink={0}
            borderRadius="16px"
            overflow="hidden"
          >
            <Image
              src={noticeThumbnail}
              alt={notice?.title}
              w="100%"
              h="100%"
              objectFit="contain"
            />
          </Box>
        )}

        <Box
          px={compact ? "8px" : "12px"}
          py={compact ? "8px" : "10px"}
          display="flex"
          flexDirection="column"
          flex="1"
        >
          <Flex direction="column" gap={compact ? 1 : 2} flex="1">
            <Text
              fontWeight="600"
              fontSize={compact ? "14px" : "16px"}
              color="#2F2F34"
              noOfLines={1}
            >
              {notice?.title}
            </Text>
            <Text
              color="#5C6670"
              fontSize={compact ? "12px" : "14px"}
              fontWeight={400}
              noOfLines={2}
            >
              {notice?.description}
            </Text>

            <Flex
              direction="row"
              align="center"
              justify="space-between"
              mt={compact ? 1 : 2}
              gap={3}
            >
              <Flex
                align="center"
                gap={2}
                alignItems="center"
                color="#798692"
                fontSize={compact ? "12px" : "14px"}
              >
                <Icon as={CalenderIcon} boxSize={compact ? 3 : 4} />
                <Text>{formatDateToYYYYMMDD(notice?.createdAt)}</Text>
              </Flex>
              <Text
                fontWeight="400"
                fontSize={compact ? "12px" : "14px"}
                textColor="#5C6670"
                noOfLines={1}
              >
                {notice?.noticeType?.name}
              </Text>
            </Flex>
          </Flex>

          {hasDocuments && (
            <Button
              size="sm"
              variant="outline"
              borderColor="#1819B4"
              color="#1819B4"
              fontWeight={600}
              fontSize="12px"
              leftIcon={
                isDownloading ? (
                  <Spinner size="xs" color="#1819B4" />
                ) : (
                  <DownloadIcon boxSize={3} color="#1819B4" />
                )
              }
              onClick={handleDownloadFiles}
              borderRadius="8px"
              height="28px"
              px="12px"
              py="4px"
              _hover={{ bg: "#F5F7FA" }}
              isDisabled={isDownloading}
              mt="8px"
              alignSelf="flex-start"
            >
              {isDownloading
                ? t("notice.downloading")
                : t("notice.downloadFiles")}
            </Button>
          )}
        </Box>
      </Box>
      <NoticeDetailsModal
        formatDateToYYYYMMDD={formatDateToYYYYMMDD}
        isOpen={isOpen}
        onClose={onClose}
        notice={notice}
      />
    </>
  );
};

export default NoticeCard;
